import type { Role } from './enums';

/**
 * SPEC §9 — the permission matrix, kept in ONE place and consumed by both
 * apps/api (RolesGuard) and apps/web (to hide what the user cannot do).
 */
export const PERMISSIONS = [
  'batch.view',
  'batch.import',
  'batch.rename',
  'batch.export',
  'batch.delete',
  'row.view',
  'row.edit',
  'row.override',
  'row.create',
  'row.remove',
  'row.approve',
  'row.unapprove',
  'asset.generate',
  'asset.regenerate',
  'asset.select_take',
  'run.abort',
  'settings.view',
  'settings.manage',
  'user.manage',
  'audit.view',
] as const;

export type Permission = (typeof PERMISSIONS)[number];

/** EMPLOYEE: everything except settings, users, batch delete and un-approve. */
const EMPLOYEE_PERMISSIONS: readonly Permission[] = [
  'batch.view',
  'batch.import',
  'batch.rename',
  'batch.export',
  'row.view',
  'row.edit',
  'row.override',
  'row.create',
  'row.remove',
  'row.approve',
  'asset.generate',
  'asset.regenerate',
  'asset.select_take',
  'run.abort',
  'settings.view',
];

/** SUPER_ADMIN: everything. */
const SUPER_ADMIN_PERMISSIONS: readonly Permission[] = PERMISSIONS;

export const ROLE_PERMISSIONS: Record<Role, readonly Permission[]> = {
  EMPLOYEE: EMPLOYEE_PERMISSIONS,
  SUPER_ADMIN: SUPER_ADMIN_PERMISSIONS,
};

export function can(role: Role | null | undefined, permission: Permission): boolean {
  if (!role) return false;
  return ROLE_PERMISSIONS[role].includes(permission);
}

export function canAll(role: Role | null | undefined, permissions: readonly Permission[]): boolean {
  return permissions.every((p) => can(role, p));
}

export function canAny(role: Role | null | undefined, permissions: readonly Permission[]): boolean {
  return permissions.some((p) => can(role, p));
}

/** SPEC §9 — the only routes reachable without a session. */
export const PUBLIC_ROUTES = ['/api/auth/login', '/api/auth/accept-invite', '/api/health'] as const;
